import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { IconBank, IconArrowRight, IconArrowLeft, IconForward, IconQrCode, IconSuccess } from '../../../icons';

interface BankDetailsStepProps {
  initialData: {
    iban: string;
    bic: string;
    bankName: string;
    accountHolder: string;
  };
  onSave: (data: Record<string, any>) => Promise<void>;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
}

const BankDetailsStep: React.FC<BankDetailsStepProps> = ({
  initialData,
  onSave,
  onNext,
  onBack,
  onSkip,
}) => {
  const { t } = useTranslation('onboarding');
  const [iban, setIban] = useState(initialData.iban);
  const [bic, setBic] = useState(initialData.bic);
  const [bankName, setBankName] = useState(initialData.bankName);
  const [accountHolder, setAccountHolder] = useState(initialData.accountHolder);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  // Auto-format IBAN (Großbuchstaben, Leerzeichen alle 4 Zeichen)
  const formatIban = (value: string) => {
    const cleaned = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    const groups = cleaned.match(/.{1,4}/g);
    return groups ? groups.join(' ') : '';
  };

  const handleIbanChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatIban(e.target.value);
    setIban(formatted);
    setError('');
  };

  // Validiere IBAN (einfache Prüfung)
  const isValidIban = (value: string): boolean => {
    const cleaned = value.replace(/\s/g, '');
    // Deutsche IBAN: DE + 2 Prüfziffern + 18 Zeichen = 22 Zeichen
    // Allgemein: 15-34 Zeichen
    return cleaned.length >= 15 && cleaned.length <= 34 && /^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/.test(cleaned);
  };

  const handleSaveAndNext = async () => {
    if (!iban.trim()) {
      // Wenn nichts eingegeben, überspringen
      onSkip();
      return;
    }

    if (!isValidIban(iban)) {
      setError(t('steps.bankDetails.errorInvalidIban'));
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      const cleanedBic = bic.trim();
      const cleanedBankName = bankName.trim();
      const cleanedAccountHolder = accountHolder.trim();

      await onSave({
        bankDetails: {
          iban: iban.replace(/\s/g, ''), // Speichere ohne Leerzeichen
          ...(cleanedBic ? { bic: cleanedBic } : {}),
          ...(cleanedBankName ? { bankName: cleanedBankName } : {}),
          ...(cleanedAccountHolder ? { accountHolder: cleanedAccountHolder } : {}),
        },
      });
      onNext();
    } catch (err) {
      setError(t('steps.bankDetails.errorSaving'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 mb-4">
          <IconBank size={32} className="text-primary" />
        </div>
        <h2 className="text-2xl font-bold text-primary mb-2">
          {t('steps.bankDetails.title')}
        </h2>
        <p className="text-muted">
          {t('steps.bankDetails.subtitle')}
        </p>
      </div>

      {/* QR-Code Benefit */}
      <div className="bg-[var(--tone-success-bg)] border border-[var(--tone-success-border)] rounded-xl p-4 mb-6">
        <div className="flex items-start">
          <IconQrCode size={24} className="text-[var(--tone-success-fg)] mt-1 mr-3 flex-shrink-0" />
          <div>
            <p className="text-[var(--tone-success-fg)] font-medium">{t('steps.bankDetails.qrCodeBenefit')}</p>
            <p className="text-[var(--tone-success-fg)] text-sm mt-1">{t('steps.bankDetails.qrCodeDescription')}</p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="space-y-4">
        {/* IBAN */}
        <div>
          <label htmlFor="iban" className="block text-sm font-medium text-secondary mb-2">
            IBAN
          </label>
          <input
            type="text"
            id="iban"
            value={iban}
            onChange={handleIbanChange}
            placeholder={t('steps.bankDetails.ibanPlaceholder')}
            className="w-full px-4 py-3 bg-surface-raised border border-hairline rounded-xl text-primary placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all font-mono tracking-wider"
            autoComplete="off"
          />
          {iban && isValidIban(iban) && (
            <p className="mt-1 text-sm text-[var(--tone-success-fg)] flex items-center">
              <IconSuccess className="mr-1" /> {t('steps.bankDetails.ibanValid')}
            </p>
          )}
        </div>

        {/* BIC */}
        <div>
          <label htmlFor="bic" className="block text-sm font-medium text-secondary mb-2">
            BIC/SWIFT <span className="text-faint">({t('labels.optional')})</span>
          </label>
          <input
            type="text"
            id="bic"
            value={bic}
            onChange={(e) => setBic(e.target.value.toUpperCase())}
            placeholder={t('steps.bankDetails.bicPlaceholder')}
            className="w-full px-4 py-3 bg-surface-raised border border-hairline rounded-xl text-primary placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all font-mono"
            autoComplete="off"
          />
        </div>

        {/* Bank Name */}
        <div>
          <label htmlFor="bankName" className="block text-sm font-medium text-secondary mb-2">
            {t('steps.bankDetails.bankNameLabel')} <span className="text-faint">({t('labels.optional')})</span>
          </label>
          <input
            type="text"
            id="bankName"
            value={bankName}
            onChange={(e) => setBankName(e.target.value)}
            placeholder={t('steps.bankDetails.bankNamePlaceholder')}
            className="w-full px-4 py-3 bg-surface-raised border border-hairline rounded-xl text-primary placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
          />
        </div>

        {/* Account Holder */}
        <div>
          <label htmlFor="accountHolder" className="block text-sm font-medium text-secondary mb-2">
            {t('steps.bankDetails.accountHolderLabel')} <span className="text-faint">({t('labels.optional')})</span>
          </label>
          <input
            type="text"
            id="accountHolder"
            value={accountHolder}
            onChange={(e) => setAccountHolder(e.target.value)}
            placeholder={t('steps.bankDetails.accountHolderPlaceholder')}
            className="w-full px-4 py-3 bg-surface-raised border border-hairline rounded-xl text-primary placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
          />
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mt-4 p-3 bg-[var(--tone-danger-bg)] border border-[var(--tone-danger-border)] rounded-lg text-[var(--tone-danger-fg)] text-sm">
          {error}
        </div>
      )}

      {/* Buttons */}
      <div className="flex justify-between mt-8">
        <button
          onClick={onBack}
          className="flex items-center px-6 py-3 text-muted hover:text-primary transition-colors"
        >
          <IconArrowLeft className="mr-2" />
          {t('common.back')}
        </button>

        <div className="flex space-x-3">
          <button
            onClick={onSkip}
            className="flex items-center px-6 py-3 text-muted hover:text-primary transition-colors"
          >
            <IconForward className="mr-2" />
            {t('common.skip')}
          </button>
          
          <button
            onClick={handleSaveAndNext}
            disabled={isSaving}
            className="flex items-center px-8 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-green-500/30 transform hover:scale-[1.02] transition-all disabled:opacity-50 disabled:transform-none"
          >
            {isSaving ? t('common.saving') : t('common.next')}
            {!isSaving && <IconArrowRight className="ml-2" />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BankDetailsStep;



